import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { getViewAsVendor } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Menu, User } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CurrencySelector from "@/components/CurrencySelector";
import LanguageSelector from "@/components/LanguageSelector";
import MobileSettingsMenu from "@/components/MobileSettingsMenu";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import { readSourceVendor } from "@/lib/sourceVendor";
import NotificationsBell from "@/components/ops/NotificationsBell";

export default function Header() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Iter 69 — when the visitor arrived via a vendor microsite, the global
  // "Browse Cars" link should auto-scope to that vendor's fleet so they
  // don't accidentally end up shopping across competitors mid-session.
  // We re-read localStorage on every render via a small effect that listens
  // to the custom "rentalcar:source-vendor-changed" event (dispatched when
  // the Browse-page "Show all" chip clears it).
  const [sourceVendor, setSourceVendor] = useState(() => readSourceVendor());
  useEffect(() => {
    const onChange = () => setSourceVendor(readSourceVendor());
    window.addEventListener("rentalcar:source-vendor-changed", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("rentalcar:source-vendor-changed", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);
  const browseHref = sourceVendor?.id ? `/cars?vendor_id=${encodeURIComponent(sourceVendor.id)}` : "/cars";

  return (
    <>
      <ImpersonationBanner />
      <header className="sticky top-0 z-40 border-b border-border/60 bg-white/80 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-2">
        <Link to="/" className="flex items-center gap-2 group min-w-0 shrink" data-testid="logo-link">
          {/* Iter 67ed — rounded-square amber "R" mark with road-inside-letter
              motif. Dark-mode variants available at /logo-dark.png and
              /logo-dark-rounded.png for use on dark hero overlays. */}
          <img
            src="/logo-192.png"
            alt="RentalCar.al"
            className="h-10 w-10 object-contain shrink-0 rounded-2xl"
            width="40"
            height="40"
          />
          <span className="font-display text-base sm:text-xl font-semibold tracking-tight truncate">
            RentalCar<span className="text-red-600">.al</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLink to={browseHref} className={({isActive}) => `text-sm font-medium ${isActive ? "text-blue-600" : "text-neutral-700 hover:text-neutral-900"}`} data-testid="nav-cars">
            {t("header.browseCars")}
            {sourceVendor?.name && (
              <span className="ml-1.5 text-[10px] uppercase tracking-wider font-semibold text-amber-700" data-testid="nav-cars-vendor-scope">· {sourceVendor.name}</span>
            )}
          </NavLink>
          <NavLink to="/become-vendor" className={({isActive}) => `text-sm font-medium ${isActive ? "text-blue-600" : "text-neutral-700 hover:text-neutral-900"}`} data-testid="nav-become-vendor">{t("header.listYourCar")}</NavLink>
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          {/* Desktop: inline selectors. Mobile: a single Globe icon menu. */}
          <div className="hidden md:flex items-center gap-2">
            <LanguageSelector />
            <CurrencySelector />
          </div>
          <MobileSettingsMenu />
          {/* Iter 98 — ops notifications for vendor / staff / admin / agent. */}
          {user && ["vendor", "vendor_staff", "admin", "agent"].includes(user.role) && <NotificationsBell />}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 border border-border rounded-full pl-3 pr-1 py-1 hover:shadow-md transition-all" data-testid="user-menu">
                  <Menu className="h-4 w-4 text-neutral-600" />
                  <div className="h-7 w-7 rounded-full bg-neutral-900 text-white flex items-center justify-center text-xs font-semibold">
                    {user.name?.[0]?.toUpperCase() || <User className="h-3 w-3" />}
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-medium">{user.name}</div>
                  <div className="text-xs text-neutral-500 capitalize">{user.role}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {user.role === "customer" && (
                  <DropdownMenuItem onClick={() => navigate("/dashboard/customer")} data-testid="menu-customer-dashboard">{t("dashboard.myBookings")}</DropdownMenuItem>
                )}
                {user.role === "vendor" && (
                  <DropdownMenuItem onClick={() => navigate("/dashboard/vendor")} data-testid="menu-vendor-dashboard">Vendor dashboard</DropdownMenuItem>
                )}
                {/* Iter 67ag — surface the Vendor dashboard menu link to
                    admins/agents while they're in view-as-vendor mode so the
                    impersonation flow has a one-click path to the role they
                    just switched into. */}
                {user.role !== "vendor" && getViewAsVendor() && (
                  <DropdownMenuItem onClick={() => navigate("/dashboard/vendor")} data-testid="menu-vendor-dashboard-impersonating">Vendor dashboard (view-as)</DropdownMenuItem>
                )}
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/dashboard/admin")} data-testid="menu-admin-dashboard">Admin panel</DropdownMenuItem>
                )}
                {user.role === "agent" && (
                  <DropdownMenuItem onClick={() => navigate("/dashboard/agent")} data-testid="menu-agent-dashboard">Agent panel</DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">{t("header.logout")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="ghost" onClick={() => navigate("/login")} className="rounded-full px-3 sm:px-4 text-sm" data-testid="header-login">{t("header.login")}</Button>
              <Button onClick={() => navigate("/register")} className="rounded-full bg-amber-500 hover:bg-amber-600 text-neutral-900 font-semibold px-3 sm:px-4 text-sm shrink-0" data-testid="header-signup">{t("header.signup")}</Button>
            </>
          )}
        </div>
      </div>
    </header>
    </>
  );
}
